import { ComponentChild } from 'preact';
import clsx from 'clsx';
import TickIcon from 'images/icons/Tick.svg';
import { forwardRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useRef } from 'preact/hooks';
import { useUniqueId } from 'utilities/use-unique-id';

import { Icon } from 'components/Icon';
import { getErrorId, InlineError } from 'components/InlineError';
import { Text } from 'components/Text';

import styles from './Checkbox.module.scss';

interface CheckboxProps {
  label: ComponentChild;
  checked?: boolean;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  value?: string;
  onChange?: (value: boolean, id: string) => void;
}

interface CheckboxHandles {
  focus: () => void;
}

export const Checkbox = forwardRef<CheckboxHandles, CheckboxProps>(
  function Checkbox(
    {
      label,
      checked,
      disabled,
      error,
      required,
      value,
      onChange,
    }: CheckboxProps,
    ref,
  ) {
    const id = useUniqueId('Checkbox');
    const inputRef = useRef<HTMLInputElement>(null);
    const className = clsx(
      styles.Checkbox,
      checked && styles.checked,
      error && styles.hasError,
      required && styles.required,
      disabled && styles.disabled,
    );

    const handleChange = useCallback(() => {
      if (!onChange || inputRef.current === null || disabled) {
        return;
      }

      onChange(inputRef.current.checked, id);
      inputRef.current.focus();
    }, [disabled, id, onChange]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      },
    }));

    const errorMarkup = error ? (
      <div className={styles.Error}>
        <InlineError fieldId={id}>{error}</InlineError>
      </div>
    ) : null;

    return (
      <div>
        <label className={className} htmlFor={id}>
          <span className={styles.Control}>
            <input
              ref={inputRef}
              id={id}
              type="checkbox"
              role="checkbox"
              checked={checked}
              disabled={disabled}
              aria-checked={checked}
              aria-describedby={error && getErrorId(id)}
              required={required}
              value={value}
              className={styles.Input}
              onClick={handleChange}
              onChange={noop}
            />
            <span className={styles.Icon}>
              <Icon source={TickIcon} color="inherit" />
            </span>
            <span className={styles.Backdrop} onClick={stopPropagation}>
              &nbsp;
            </span>
          </span>
          <span className={styles.Label}>
            <Text
              as="span"
              size="small"
              variation={disabled ? 'subdued' : undefined}
            >
              {label}
            </Text>
          </span>
        </label>
        {errorMarkup}
      </div>
    );
  },
);

function stopPropagation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}
